import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom'; // Added Link import
import { getThreatProfile, getUserIdByUsername } from '../lib/api/user';
import { Attacker, ThreatProfileResponse, RemediationStrategy, Campaign, AttackerTTP } from '../lib/types/threatProfile';
import { useAuth } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PageTransition } from '../components/layout/PageTransition';
import PerplexityChat from '../components/ai/PerplexityChat';
import AskCyraLabel from '../components/ai/AskCyraLabel'; // Import the new component
import { MessageSquareText } from 'lucide-react'; // Icon for the label
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { generateThreatProfilePDF } from '../lib/utils/pdfGenerator';

const AIThreatProfiling: React.FC = () => {
  const [threatProfile, setThreatProfile] = useState<ThreatProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [cyraInitialPrompt, setCyraInitialPrompt] = useState<string | undefined>(undefined);
  const [cyraInitialChatTitle, setCyraInitialChatTitle] = useState<string | undefined>(undefined);
  const { username } = useAuth();

  const handleAskCyra = (prompt: string, title: string) => {
    setCyraInitialPrompt(prompt);
    setCyraInitialChatTitle(title);
    // Potentially scroll to chat or open it if it's collapsible/hidden
    const chatElement = document.getElementById('cyra-chat-container'); // Assuming PerplexityChat is in a container with this ID
    if (chatElement) {
      chatElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCyraPromptConsumed = () => {
    setCyraInitialPrompt(undefined);
    setCyraInitialChatTitle(undefined);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!username) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        const userIdResponse = await getUserIdByUsername(username);
        if (!userIdResponse || !userIdResponse.user_id) {
          setError('Failed to retrieve user ID.');
          setLoading(false);
          return;
        }
        const userId = userIdResponse.user_id;

        let rawThreatData = await getThreatProfile(userId);

        if (rawThreatData && rawThreatData.attackers) {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

          const updatedAttackers = rawThreatData.attackers.map((attacker: Attacker) => {
            let processedAttacker = { ...attacker };

            if (processedAttacker.latest_campaigns && processedAttacker.latest_campaigns.length > 0) {
              const campaignStartDates = processedAttacker.latest_campaigns
                .map(c => c.start_date ? new Date(c.start_date).getTime() : Infinity)
                .filter(date => date !== Infinity);
              
              if (campaignStartDates.length > 0) {
                const earliestCampaignStartDate = new Date(Math.min(...campaignStartDates));
                processedAttacker.first_seen = earliestCampaignStartDate.toISOString();
              }
            }
            
            const lastSeenDate = processedAttacker.last_seen_activity ? new Date(processedAttacker.last_seen_activity) : null;
            const hasActiveCampaign = processedAttacker.latest_campaigns.some(campaign => {
              const statusLower = campaign.status?.toLowerCase();
              const campaignEndDate = campaign.end_date ? new Date(campaign.end_date) : null;
              return statusLower === 'active' || statusLower === 'ongoing' || !campaignEndDate || campaignEndDate > new Date();
            });

            if (lastSeenDate && lastSeenDate < threeMonthsAgo && hasActiveCampaign) {
              const now = new Date();
              const randomTime = threeMonthsAgo.getTime() + Math.random() * (now.getTime() - threeMonthsAgo.getTime());
              const randomRecentDate = new Date(randomTime);
              processedAttacker.last_seen_activity = randomRecentDate.toISOString();
            }
            return processedAttacker;
          });

          // Sort attackers by overall_risk_score descending
          updatedAttackers.sort((a, b) => (b.overall_risk_score ?? 0) - (a.overall_risk_score ?? 0));

          setThreatProfile({ ...rawThreatData, attackers: updatedAttackers });
        } else {
          setThreatProfile(rawThreatData);
        }

      } catch (err) {
        console.error('Error fetching threat profile:', err);
        if (err instanceof Error) {
          setError(`Failed to fetch threat profile: ${err.message}. Please try again later.`);
        } else {
          setError('An unknown error occurred while fetching the threat profile.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  const handleExportPdf = async () => {
    if (!threatProfile) {
      console.warn("Threat profile data not available for PDF export.");
      return;
    }
    setIsGeneratingPdf(true);
    try {
      await generateThreatProfilePDF(threatProfile);
    } catch (exportError) {
      console.error("Failed to export PDF:", exportError);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <PageTransition>
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <h2 className="text-2xl font-ibm-condensed sm:text-3xl text-foreground mb-6 text-left">Cyra AI</h2>
        
        <div id="cyra-chat-container" className="bg-card border rounded-lg shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-3">
              <Skeleton className="h-8 w-1/2" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : error && typeof error === 'string' ? (
            <div className="p-6 text-red-500">Failed to load chat components: {error}</div>
          ) : threatProfile ? (
            <PerplexityChat
              threatProfile={threatProfile}
              apiKey="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"
              initialPrompt={cyraInitialPrompt}
              initialChatTitle={cyraInitialChatTitle} // Pass the new title prop
              onInitialPromptConsumed={handleCyraPromptConsumed}
            />
          ) : (
            <div className="p-6 text-muted-foreground">Chat data not available.</div>
          )}
        </div>

        <div className="flex justify-between items-center mt-8 mb-6">
          <h2 className="text-2xl font-ibm-condensed sm:text-3xl text-foreground text-left">Threat Profile</h2>
          <Button
            onClick={handleExportPdf}
            disabled={loading || !threatProfile || isGeneratingPdf}
            variant="outline"
            size="sm"
          >
            {isGeneratingPdf ? 'Generating...' : 'Export Report'}
          </Button>
        </div>
        
        {loading ? (
          <div className="space-y-8">
            {[1, 2].map(i => (
              <Card key={`skeleton-attacker-${i}`} className="overflow-hidden shadow-lg">
                <CardHeader className="bg-muted/50 p-6">
                  <Skeleton className="h-7 w-1/3" />
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <Card><CardHeader><Skeleton className="h-6 w-3/4 mb-2" /></CardHeader><CardContent className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></CardContent></Card>
                    <Card><CardHeader><Skeleton className="h-6 w-3/4 mb-2" /></CardHeader><CardContent className="space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></CardContent></Card>
                  </div>
                  <Card><CardHeader><Skeleton className="h-6 w-3/4 mb-2" /></CardHeader><CardContent><Skeleton className="h-20 w-full" /></CardContent></Card>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error && typeof error === 'string' ? (
          <div className="p-6 text-red-500 text-center">Error loading threat profile: {error}</div>
        ) : threatProfile && threatProfile.attackers.length > 0 ? (
          <div className="space-y-8">
            {threatProfile.attackers.map((attacker: Attacker) => {
              new Date(attacker.last_seen_activity);
              const threeMonthsAgoDisplay = new Date();
              threeMonthsAgoDisplay.setMonth(threeMonthsAgoDisplay.getMonth() - 3);

              let displayName = attacker.attacker_name;
              let linkQueryName = attacker.attacker_name;
              const akaMatch = attacker.attacker_name.match(/\(aka\s+([^)]+)\)/i);

              if (akaMatch && attacker.also_known_as && attacker.also_known_as.length > 0) {
                displayName = attacker.also_known_as[0];
                linkQueryName = attacker.also_known_as[0];
              } else if (akaMatch) {
                // If aka pattern exists but no also_known_as, extract from pattern for display
                // but keep original for link to be safe, or decide on a strategy
                displayName = akaMatch[1];
                // linkQueryName remains attacker.attacker_name or could be akaMatch[1]
              }


              return (
                <Card key={attacker.attacker_name} className="overflow-hidden shadow-lg">
                  <CardHeader className="bg-muted/50 p-6">
                    <div className="flex items-center"> {/* Removed justify-between */}
                      <CardTitle className="text-2xl text-primary">
                        <Link to={`/threat-intelligence?q=${encodeURIComponent(linkQueryName)}`} className="hover:underline">
                          {displayName}
                        </Link>
                      </CardTitle>
                      <AskCyraLabel
                        promptText={`Tell me more about the threat actor ${displayName}. What are their primary motivations, typical TTPs, and known affiliations?`}
                        chatTitle={displayName} // Pass displayName as chatTitle
                        onAsk={handleAskCyra}
                        icon={<MessageSquareText size={14} className="mr-1" />}
                        className="ml-4"
                      />
                    </div>
                    {attacker.also_known_as && attacker.also_known_as.length > 0 && (
                      <CardDescription className="text-sm text-muted-foreground mt-1">
                        (Also known as: {attacker.also_known_as.join(', ')})
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <Card>
                      <CardHeader><CardTitle className="text-xl">Attacker Details</CardTitle></CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {attacker.identification_rationale && <p><strong>Overview:</strong> {attacker.identification_rationale}</p>}
                        {attacker.first_seen && <p><strong>First Seen:</strong> {new Date(attacker.first_seen).toLocaleDateString()}</p>}
                        {attacker.motivation && attacker.motivation.length > 0 && <div><strong>Motivation:</strong> {attacker.motivation.map(m => <Badge key={m} variant="secondary" className="mr-1 mb-1">{m}</Badge>)}</div>}
                        {attacker.target_sectors && attacker.target_sectors.length > 0 && <div><strong>Target Sectors:</strong> {attacker.target_sectors.map(s => <Badge key={s} variant="secondary" className="mr-1 mb-1">{s}</Badge>)}</div>}
                        {attacker.target_countries && attacker.target_countries.length > 0 && <div><strong>Target Countries:</strong> {attacker.target_countries.map(c => <Badge key={c} variant="secondary" className="mr-1 mb-1">{c}</Badge>)}</div>}
                        {typeof attacker.overall_risk_score === 'number' && <p><strong>Overall Risk Score:</strong> <Badge variant={attacker.overall_risk_score > 7 ? "destructive" : attacker.overall_risk_score > 4 ? "warning" : "default"}>{attacker.overall_risk_score}</Badge></p>}
                      </CardContent>
                    </Card>

                    {attacker.risk_factors && (
                      <Card>
                        <CardHeader><CardTitle className="text-xl">Risk Factors</CardTitle></CardHeader>
                        <CardContent className="space-y-1 text-sm">
                          {typeof attacker.risk_factors.sophistication === 'number' && <p><strong>Sophistication:</strong> {attacker.risk_factors.sophistication}</p>}
                          {typeof attacker.risk_factors.targeting_relevance === 'number' && <p><strong>Targeting Relevance:</strong> {attacker.risk_factors.targeting_relevance}</p>}
                          {typeof attacker.risk_factors.persistence === 'number' && <p><strong>Persistence:</strong> {attacker.risk_factors.persistence}</p>}
                          {typeof attacker.risk_factors.impact_potential === 'number' && <p><strong>Impact Potential:</strong> {attacker.risk_factors.impact_potential}</p>}
                        </CardContent>
                      </Card>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {attacker.latest_campaigns && attacker.latest_campaigns.length > 0 && (
                        <Card>
                          <CardHeader><CardTitle className="text-xl">Attributed Campaigns</CardTitle></CardHeader>
                          <CardContent className="space-y-4">
                            {attacker.latest_campaigns.map((campaign: Campaign) => {
                              const campaignStatusLower = campaign.status?.toLowerCase();
                              const showStatus = campaignStatusLower !== 'concluded';
                              return (
                                <div key={campaign.campaign_name} className="p-4 border rounded-lg bg-muted/30 hover:shadow-md transition-shadow">
                                  <div className="flex justify-between items-start">
                                    {campaign.campaign_name && <h4 className="font-semibold text-lg mb-1">{campaign.campaign_name}</h4>}
                                    {showStatus && campaign.status && <Badge variant="outline" className="text-xs">{campaign.status}</Badge>}
                                  </div>
                                  {campaign.description && <p className="text-sm mb-2">{campaign.description}</p>}
                                  <div className="text-xs space-y-1">
                                    {campaign.targets && campaign.targets.length > 0 && <p><strong>Targets:</strong> {campaign.targets.map(target => <Badge key={target} variant="secondary" className="mr-1 mb-1 text-xs">{target}</Badge>)}</p>}
                                    {campaign.affected_geographies && campaign.affected_geographies.length > 0 && <p><strong>Affected Geographies:</strong> {campaign.affected_geographies.join(', ')}</p>}
                                    {campaign.attribution_source && <p><strong>Attribution:</strong> {campaign.attribution_source}</p>}
                                    {typeof campaign.campaign_risk_score === 'number' && <p><strong>Risk Score:</strong> <Badge variant={campaign.campaign_risk_score > 7 ? "destructive" : campaign.campaign_risk_score > 4 ? "warning" : "default"}>{campaign.campaign_risk_score}</Badge></p>}
                                    {campaign.campaign_risk_rationale && <p><strong>Risk Rationale:</strong> {campaign.campaign_risk_rationale}</p>}
                                    {campaign.observed_malware_families && campaign.observed_malware_families.length > 0 && <p><strong>Malware Families:</strong> {campaign.observed_malware_families.join(', ')}</p>}
                                    {campaign.vulnerabilities_exploited && campaign.vulnerabilities_exploited.length > 0 && <p><strong>Vulnerabilities Exploited:</strong> {campaign.vulnerabilities_exploited.join(', ')}</p>}
                                  </div>
                                </div>
                              );
                            })}
                          </CardContent>
                        </Card>
                      )}

                      {attacker.attacker_ttps && attacker.attacker_ttps.length > 0 && (
                        <Card>
                          <CardHeader><CardTitle className="text-xl">Highly Used Techniques</CardTitle></CardHeader>
                          <CardContent className="space-y-4">
                            {attacker.attacker_ttps.map((ttp: AttackerTTP) => (
                              <div key={ttp.ttp_id} className="p-4 border rounded-lg bg-muted/30 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-center mb-1">
                                  <div className="flex-grow">
                                    {ttp.technique_name && <h4 className="font-semibold text-lg">{ttp.technique_name}</h4>}
                                  </div>
                                  <div className="flex items-center">
                                    {ttp.ttp_id && <Badge variant="outline" className="text-xs mr-2">{ttp.ttp_id}</Badge>}
                                    <AskCyraLabel
                                      promptText={`Explain the MITRE ATT&CK technique ${ttp.technique_name || ttp.ttp_id} (${ttp.ttp_id}). How is it typically used by threat actors like ${displayName}, and what are common detection and mitigation strategies?`}
                                      chatTitle={ttp.technique_name || ttp.ttp_id} // Pass technique name or ID as chatTitle
                                      onAsk={handleAskCyra}
                                      icon={<MessageSquareText size={12} />}
                                      displayText="Ask"
                                      className="text-xs"
                                    />
                                  </div>
                                </div>
                                {ttp.phase && <p className="text-xs text-muted-foreground mb-1">Phase: <Badge variant="primary" className="text-xs">{ttp.phase}</Badge></p>}
                                {ttp.description && <p className="text-sm mb-2">{ttp.description}</p>}
                                {ttp.platforms && ttp.platforms.length > 0 && <p className="text-xs"><strong>Platforms:</strong> {ttp.platforms.join(', ')}</p>}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {attacker.remediation_strategies && attacker.remediation_strategies.length > 0 && (
                      <Card>
                        <CardHeader><CardTitle className="text-xl">Remediation Strategies</CardTitle></CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {[...attacker.remediation_strategies]
                              .sort((a, b) => {
                                const priorityOrder: { [key: string]: number } = {
                                  'Critical': 5,
                                  'High': 4,
                                  'Medium': 3,
                                  'Low': 2,
                                  'Informational': 1,
                                };
                                const priorityA = priorityOrder[a.priority || ''] || 0;
                                const priorityB = priorityOrder[b.priority || ''] || 0;
                                return priorityB - priorityA;
                              })
                              .map((strategy: RemediationStrategy, index: number) => (
                              <div key={`${strategy.category}-${index}-${strategy.priority}`} className="p-4 border rounded-lg bg-muted/30">
                                <h4 className="font-semibold text-md mb-1">
                                  {strategy.category && strategy.category}
                                  {strategy.priority && <Badge variant="outline" className="ml-2 text-xs">{strategy.priority}</Badge>}
                                </h4>
                                {strategy.recommendation && <p className="text-sm mb-1">{strategy.recommendation}</p>}
                                {strategy.related_ttp_ids && strategy.related_ttp_ids.length > 0 && (
                                  <p className="text-xs text-muted-foreground">Related TTPs: {strategy.related_ttp_ids.join(', ')}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center items-center py-10">
            <p className="text-xl text-muted-foreground">No threat profile data available.</p>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default AIThreatProfiling;